import { useEffect, useMemo } from 'react';
import { useKeyPress } from '@xyflow/react';
import { SweepCanvasSelectionMode } from '../canvas-controls/CanvasControlsCtx';

interface UseKeyboardSelectionModeProps {
  selectionMode: SweepCanvasSelectionMode;
  setSelectionMode: (mode: SweepCanvasSelectionMode) => void;
  disableMultiSelection?: boolean;
}

interface UseKeyboardSelectionModeReturn {
  selectionMode: SweepCanvasSelectionMode;
}

/**
 * Hook that manages keyboard-based selection mode behavior for canvas controls.
 *
 * Features:
 * - Space key: temporarily switches to pan mode while held
 * - Shift key: temporarily switches to select mode while held
 * - V key: switches to select mode
 * - H key: switches to pan mode
 * - Automatically disables multi-selection when disableMultiSelection is true
 */
export const useKeyboardSelectionMode = ({
  selectionMode,
  setSelectionMode,
  disableMultiSelection = false,
}: UseKeyboardSelectionModeProps): UseKeyboardSelectionModeReturn => {
  const spacePressed = useKeyPress('Space');
  const shiftPressed = useKeyPress('Shift');
  const vPressed = useKeyPress('v');
  const hPressed = useKeyPress('h');

  // Calculate the temporary mode based on space and shift keys
  const temporaryMode = useMemo(() => {
    if (spacePressed || shiftPressed) {
      return spacePressed
        ? SweepCanvasSelectionMode.panOnDrag
        : SweepCanvasSelectionMode.selectOnDrag;
    }
    return undefined;
  }, [spacePressed, shiftPressed]);

  // Handle disableMultiSelection effect
  useEffect(() => {
    if (disableMultiSelection) {
      setSelectionMode(SweepCanvasSelectionMode.panOnDrag);
    }
  }, [disableMultiSelection, setSelectionMode]);

  // Handle keyboard shortcuts for mode switching
  useEffect(() => {
    if (disableMultiSelection) {
      return;
    }

    if (vPressed) {
      setSelectionMode(SweepCanvasSelectionMode.selectOnDrag);
    }
    if (hPressed) {
      setSelectionMode(SweepCanvasSelectionMode.panOnDrag);
    }
  }, [vPressed, hPressed, setSelectionMode, disableMultiSelection]);

  return {
    selectionMode: temporaryMode ?? selectionMode,
  };
};
